import { getRequestInstance } from "api/services/utils";
import {
  VentureNoteTypeCreateDocument,
  VentureNoteTypeDeleteDocument,
  VentureNoteTypeGetByIdDocument,
  VentureNoteTypeListDocument,
  VentureNoteTypeUpdateDocument,
  VentureNoteTypesPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { ResourceEnum, ScopeEnum } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

import {
  ICreateNoteTypeVars,
  IGetNoteTypeByIdReturnType,
  IGetNotesListArgs,
  INoteTypeModel,
  INotesListReturnType,
  IUpdateNoteTypeVars,
} from "api/services/library/note-type";

export async function getVentureNoteTypesList(
  variables: IGetNotesListArgs
): Promise<INotesListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureNoteTypesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureNoteTypesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureNoteType(
  variables: ICreateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureNoteTypeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all note type queries so they refetch with the new item
    executeEviction(client, {
      typename: "VentureNoteType",
      fieldNames: ["ventureNoteTypesPaginated", "ventureNoteTypeList"],
    });

    return res?.data?.ventureNoteTypeCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureNoteType(
  variables: IUpdateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureNoteTypeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "VentureNoteType",
      id: variables.noteTypeId.toString(),
      fieldNames: [
        "ventureNoteTypesPaginated",
        "ventureNoteTypeList",
        "ventureNoteTypeGetById",
      ],
    });

    return res?.data?.ventureNoteTypeUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureNoteType(
  noteTypeId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureNoteTypeDeleteDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "VentureNoteType",
      id: noteTypeId.toString(),
      fieldNames: [
        "ventureNoteTypesPaginated",
        "ventureNoteTypeList",
        "ventureNoteTypeGetById",
      ],
    });

    return Boolean(res?.data?.ventureNoteTypeDelete);
  } catch (error) {
    throw error;
  }
}

export async function getVentureNoteType(
  noteTypeId: number
): Promise<IGetNoteTypeByIdReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureNoteTypeGetByIdDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureNoteTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function getVentureNoteTypes(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Pick<INoteTypeModel, "noteTypeId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureNoteTypeListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureNoteTypeList ?? [];
  } catch (error) {
    throw error;
  }
}
